import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { School } from 'models/school';
import { SchoolEvent } from 'models/event';
import { getSchoolEventOutcome } from 'helpers/eventOutcome';
import { Sport } from 'models/sport';
import { DEFAULT_LIMIT } from 'consts/table';
import { EVENT_TYPES_SERVER_TO_CLIENT_MAPPING } from 'consts/event';
import { CLUB_GENDER_SERVER_TO_CLIENT_MAPPING } from 'consts/club';
import { AGE_GROUPS } from 'consts/school';
import { getFilterForSchoolFixtures, getFilterForSchoolResultsWithoutClubsAndTournaments } from 'helpers/filters';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getEventGeneratedName, isEventStatusCanceled } from 'helpers/event';
import { getEventResultForCalendar } from 'helpers/calendar';
import { getSchoolEventLocation } from 'helpers/eventView';
import { getSchoolEvents, getSchoolEventsCount, getSchoolSports } from 'services/school';
import Loader from 'components/loader';
import './FixturesResults.scss';

interface Props {
   school: School;
   mode: 'fixtures' | 'results';
}

interface FiltersState {
   sport: string;
   age: string;
   gender: string;
   eventType: string;
   venue: string;
   dates: string;
   from: string;
   to: string;
}

const EMPTY_FILTERS: FiltersState = {
   sport: '', age: '', gender: '', eventType: '', venue: '', dates: '', from: '', to: '',
};

const FILTER_LABELS: Record<keyof FiltersState, string> = {
   sport: 'Sport', age: 'Age', gender: 'Gender', eventType: 'Kind', venue: 'Venue', dates: 'Dates', from: 'From', to: 'To',
};

function dateInputValue(value: Date) {
   const year = value.getFullYear();
   const month = String(value.getMonth() + 1).padStart(2, '0');
   const day = String(value.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
}

function getSeasonStart() {
   const today = new Date();
   return new Date(today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1, 8, 1);
}

function getDateBounds(mode: Props['mode'], filters: FiltersState) {
   const today = new Date();
   const fromStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
   let from: Date | undefined;
   let to: Date | undefined;

   switch (filters.dates) {
      case 'NEXT_7':
         from = fromStart;
         to = new Date(fromStart); to.setDate(to.getDate() + 7);
         break;
      case 'NEXT_30':
         from = fromStart;
         to = new Date(fromStart); to.setDate(to.getDate() + 30);
         break;
      case 'LAST_30':
         to = today;
         from = new Date(fromStart); from.setDate(from.getDate() - 30);
         break;
      case 'THIS_SEASON':
         from = mode === 'results' ? getSeasonStart() : fromStart;
         to = mode === 'results' ? today : undefined;
         break;
      case 'CUSTOM':
         from = filters.from ? new Date(`${filters.from}T00:00:00`) : undefined;
         to = filters.to ? new Date(`${filters.to}T23:59:59`) : undefined;
         break;
   }

   return { from, to };
}

function buildWhere(mode: Props['mode'], filters: FiltersState) {
   const where: any = {};
   if (filters.sport) where.sportId = { $in: [filters.sport] };
   if (filters.age) where.ages = { $in: [Number(filters.age)] };
   if (filters.gender) where.gender = filters.gender;
   if (filters.eventType) where.eventType = filters.eventType;
   if (filters.venue) where['venue.venueType'] = filters.venue;

   const bounds = getDateBounds(mode, filters);
   if (bounds.from || bounds.to) {
      where.startTime = {};
      if (bounds.from) where.startTime.$gte = bounds.from.toISOString();
      if (bounds.to) where.startTime.$lte = bounds.to.toISOString();
   }
   return where;
}

function filterFromParams(params: URLSearchParams): FiltersState {
   return Object.keys(EMPTY_FILTERS).reduce((result, key) => ({
      ...result,
      [key]: params.get(key) || '',
   }), { ...EMPTY_FILTERS });
}

function formatDateGroup(date: string) {
   return new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(date));
}

function formatDay(date: string) {
   return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(date));
}

function formatTime(date: string) {
   return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date(date));
}

function getFilterDisplayValue(key: keyof FiltersState, value: string, school: School, sports: Sport[]) {
   switch (key) {
      case 'sport': return sports.find(sport => sport.id === value)?.name || value;
      case 'age': return getAgesGroupFormatted({ ages: [Number(value)] }, school);
      case 'gender': return CLUB_GENDER_SERVER_TO_CLIENT_MAPPING[value as keyof typeof CLUB_GENDER_SERVER_TO_CLIENT_MAPPING] || value;
      case 'eventType': return EVENT_TYPES_SERVER_TO_CLIENT_MAPPING[value as keyof typeof EVENT_TYPES_SERVER_TO_CLIENT_MAPPING] || value;
      case 'venue': return value === 'HOME' ? 'Home' : value === 'AWAY' ? 'Away' : 'Custom venue';
      case 'dates': return ({ NEXT_7: 'Next 7 days', NEXT_30: 'Next 30 days', LAST_30: 'Last 30 days', THIS_SEASON: 'This season', CUSTOM: 'Custom dates' } as any)[value] || value;
      case 'from':
      case 'to': return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`));
      default: return value;
   }
}

const FixturesResults: React.FC<Props> = ({ school, mode }) => {
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const [items, setItems] = useState<SchoolEvent[]>([]);
   const [sports, setSports] = useState<Sport[]>([]);
   const [count, setCount] = useState(0);
   const [unfilteredCount, setUnfilteredCount] = useState(0);
   const [isLoading, setIsLoading] = useState(true);
   const [isLoadingMore, setIsLoadingMore] = useState(false);
   const [loadError, setLoadError] = useState(false);
   const filters = useMemo(() => filterFromParams(searchParams), [searchParams]);
   const activeFilters = (Object.entries(filters) as [keyof FiltersState, string][]).filter(([, value]) => Boolean(value));
   const effectiveDates = filters.dates || (mode === 'fixtures' ? 'NEXT_30' : 'THIS_SEASON');

   const getQuery = (skip = 0, useFilters = true) => {
      const actualFilters = useFilters ? { ...filters, dates: effectiveDates } : EMPTY_FILTERS;
      const where = buildWhere(mode, actualFilters);
      const base = mode === 'fixtures'
         ? getFilterForSchoolFixtures(school.id, where)
         : getFilterForSchoolResultsWithoutClubsAndTournaments(where);
      return { ...base, skip };
   };

   useEffect(() => {
      let isActive = true;
      setIsLoading(true);
      setLoadError(false);

      Promise.all([
         getSchoolEvents(school.id, getQuery()),
         getSchoolEventsCount(school.id, getQuery()),
         getSchoolEventsCount(school.id, getQuery(0, false)),
         getSchoolSports(school),
      ])
         .then(([events, countResult, baseCountResult, sportItems]) => {
            if (!isActive) return;
            setItems(Array.isArray(events) ? events : []);
            setCount(countResult?.count || 0);
            setUnfilteredCount(baseCountResult?.count || 0);
            setSports(Array.isArray(sportItems) ? sportItems : []);
         })
         .catch(() => {
            if (isActive) setLoadError(true);
         })
         .finally(() => {
            if (isActive) setIsLoading(false);
         });

      return () => { isActive = false; };
   }, [school.id, mode, searchParams.toString()]);

   const updateFilter = (key: keyof FiltersState, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value); else next.delete(key);
      if (key === 'dates' && value !== 'CUSTOM') {
         next.delete('from');
         next.delete('to');
      }
      setSearchParams(next, { replace: true });
   };

   const clearFilters = () => setSearchParams({}, { replace: true });
   const loadMore = () => {
      setIsLoadingMore(true);
      getSchoolEvents(school.id, getQuery(items.length))
         .then(events => setItems(current => [...current, ...(Array.isArray(events) ? events : [])]))
         .finally(() => setIsLoadingMore(false));
   };

   const groupedItems = useMemo(() => {
      const groups = new Map<string, SchoolEvent[]>();
      items.forEach(event => {
         const key = dateInputValue(new Date(event.startTime));
         groups.set(key, [...(groups.get(key) || []), event]);
      });
      return Array.from(groups, ([date, events]) => ({ date, events }));
   }, [items]);

   const cancellationsToday = items.filter(event =>
      isEventStatusCanceled(event) && dateInputValue(new Date(event.updatedAt)) === dateInputValue(new Date())
   );
   const availableAges = [...new Set(school.availableAges || [])].sort((a, b) => a - b);
   const isFilteredEmpty = items.length === 0 && activeFilters.length > 0 && unfilteredCount > 0;

   if (isLoading) return <div className='school-fixtures-results__loading'><Loader /></div>;

   return (
      <main className='school-fixtures-results'>
         <header className='school-fixtures-results__heading'>
            <div>
               <h1>{mode === 'fixtures' ? 'Fixtures' : 'Results'}</h1>
               <p>{mode === 'fixtures' ? 'Upcoming school sport, match times and venues.' : 'The latest scores and performances from school sport.'}</p>
            </div>
            <div className='school-fixtures-results__heading-actions'>
               <div className='school-fixtures-results__switch' aria-label='Fixtures or results'>
                  <Link className={mode === 'fixtures' ? 'is-active' : ''} to={`/fixtures?${searchParams.toString()}`}>Fixtures</Link>
                  <Link className={mode === 'results' ? 'is-active' : ''} to={`/results?${searchParams.toString()}`}>Results</Link>
               </div>
               <Link className='school-fixtures-results__subscribe' to='/subscribe'><CalendarMonthRoundedIcon /> Subscribe</Link>
            </div>
         </header>

         {cancellationsToday.length > 0 && (
            <aside className='school-fixtures-results__cancellations'>
               <ErrorOutlineRoundedIcon aria-hidden='true' />
               <div>
                  <strong>{cancellationsToday.length} {cancellationsToday.length === 1 ? 'fixture was' : 'fixtures were'} cancelled today</strong>
                  <span>{cancellationsToday.slice(0, 2).map(event => getEventGeneratedName(event, school.id)).filter(Boolean).join(' · ')}</span>
               </div>
            </aside>
         )}

         <section className='school-fixtures-results__filters' aria-label='Filter events'>
            <div className='school-fixtures-results__filter-fields'>
               <label><span>Sport</span><select value={filters.sport} onChange={event => updateFilter('sport', event.target.value)}><option value=''>All sports</option>{sports.map(sport => <option key={sport.id} value={sport.id}>{sport.name}</option>)}</select></label>
               <label><span>Year group</span><select value={filters.age} onChange={event => updateFilter('age', event.target.value)}><option value=''>All years</option>{availableAges.map(age => <option key={age} value={age}>{getAgesGroupFormatted({ ages: [age] }, school)}</option>)}</select></label>
               <label><span>Gender</span><select value={filters.gender} onChange={event => updateFilter('gender', event.target.value)}><option value=''>All</option><option value='MALE_ONLY'>Boys</option><option value='FEMALE_ONLY'>Girls</option><option value='MIXED'>Mixed</option></select></label>
               <label><span>Kind</span><select value={filters.eventType} onChange={event => updateFilter('eventType', event.target.value)}><option value=''>All events</option><option value='EXTERNAL_SCHOOLS'>Inter-schools</option><option value='INTERNAL_TEAMS'>Internal</option><option value='INTERNAL_HOUSES'>House</option></select></label>
               <label><span>Venue</span><select value={filters.venue} onChange={event => updateFilter('venue', event.target.value)}><option value=''>Home and away</option><option value='HOME'>Home only</option><option value='AWAY'>Away only</option><option value='CUSTOM'>Custom venue</option></select></label>
               <label><span>Dates</span><select value={effectiveDates} onChange={event => updateFilter('dates', event.target.value)}>{mode === 'fixtures' ? <><option value='NEXT_30'>Next 30 days</option><option value='NEXT_7'>Next 7 days</option><option value='THIS_SEASON'>Rest of season</option></> : <><option value='THIS_SEASON'>This season</option><option value='LAST_30'>Last 30 days</option></>}<option value='CUSTOM'>Choose dates…</option></select></label>
               {effectiveDates === 'CUSTOM' && <><label><span>From</span><input type='date' value={filters.from} onChange={event => updateFilter('from', event.target.value)} /></label><label><span>To</span><input type='date' value={filters.to} onChange={event => updateFilter('to', event.target.value)} /></label></>}
            </div>
            {activeFilters.length > 0 && (
               <div className='school-fixtures-results__chips'>
                  <span>{activeFilters.length} {activeFilters.length === 1 ? 'filter' : 'filters'}:</span>
                  {activeFilters.map(([key, value]) => (
                     <button key={key} type='button' onClick={() => updateFilter(key, '')}>{FILTER_LABELS[key]}: {getFilterDisplayValue(key, value, school, sports)} <CloseRoundedIcon /></button>
                  ))}
                  <button type='button' className='is-clear' onClick={clearFilters}>Clear all</button>
               </div>
            )}
         </section>

         {loadError ? (
            <section className='school-fixtures-results__empty' role='alert'><CalendarMonthRoundedIcon /><h2>Events could not be loaded</h2><p>Please refresh the page and try again.</p></section>
         ) : items.length === 0 ? (
            <section className='school-fixtures-results__empty'>
               <CalendarMonthRoundedIcon aria-hidden='true' />
               <h2>{isFilteredEmpty ? 'Nothing matches these filters' : mode === 'fixtures' ? 'No fixtures scheduled yet' : 'No results published yet'}</h2>
               <p>{isFilteredEmpty ? `There are ${unfilteredCount} ${mode} available, but none match this combination. Try widening the dates or clearing the filters.` : mode === 'fixtures' ? 'New fixtures will appear here as soon as the school publishes them.' : 'Results will appear here after the school confirms and publishes them.'}</p>
               <div>{isFilteredEmpty ? <button type='button' onClick={clearFilters}>Clear all filters</button> : <Link to='/subscribe'><NotificationsNoneRoundedIcon /> Subscribe for updates</Link>}<Link className='is-secondary' to={mode === 'fixtures' ? '/results' : '/fixtures'}>{mode === 'fixtures' ? 'See recent results' : 'See upcoming fixtures'}</Link></div>
            </section>
         ) : (
            <>
               <div className='school-fixtures-results__groups'>
                  {groupedItems.map(group => (
                     <section key={group.date}>
                        <div className='school-fixtures-results__group-heading'><h2>{formatDateGroup(group.date)}</h2><span>{group.events.length} {group.events.length === 1 ? 'event' : 'events'}</span></div>
                        <div className='school-fixtures-results__grid'>
                           {group.events.map(event => {
                              const eventName = getEventGeneratedName(event, school.id) || event.sport?.name || 'School event';
                              const venue = getSchoolEventLocation(event, school.id, school.postcode?.id || '');
                              const isHome = venue.toLowerCase().startsWith('home');
                              const isCancelled = isEventStatusCanceled(event);
                              const result = mode === 'results' ? getEventResultForCalendar(event, school.id) : '';
                              const outcome = mode === 'results' ? getSchoolEventOutcome(event, school.id) : '';
                              return (
                                 <button type='button' className='school-fixtures-results__card' key={event.id} onClick={() => navigate(`/event/${event.id}`)}>
                                    <span className='school-fixtures-results__date'><strong>{formatDay(event.startTime)}</strong><span>{formatTime(event.startTime)}</span></span>
                                    {outcome && <span className={`school-fixtures-results__outcome is-${outcome.toLowerCase()}`}>{outcome}</span>}
                                    <span className='school-fixtures-results__event'>
                                       <span className='school-fixtures-results__badges'><em>{event.sport?.name || 'Sport'}</em><em className={isHome ? 'is-home' : 'is-away'}>{isHome ? 'Home' : 'Away'}</em>{isCancelled && <em className='is-cancelled'>Cancelled</em>}</span>
                                       <strong>{eventName}</strong>
                                       <span className='school-fixtures-results__meta'><b>{mode === 'results' ? result || 'View results' : formatTime(event.startTime)}</b><span>{getAgesGroupFormatted({ ages: [...(event.ages || [])] }, school)}</span><span>{venue}</span></span>
                                    </span>
                                    <ArrowForwardRoundedIcon className='school-fixtures-results__arrow' aria-hidden='true' />
                                 </button>
                              );
                           })}
                        </div>
                     </section>
                  ))}
               </div>
               {items.length < count && <div className='school-fixtures-results__more'><button type='button' disabled={isLoadingMore} onClick={loadMore}>{isLoadingMore ? 'Loading…' : `Show more ${mode}`}</button></div>}
            </>
         )}
      </main>
   );
};

export default FixturesResults;
